import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";

import { userQueryKeys } from "./query-keys";

import { instance } from "@/lib/axios/instance";
import { GetUserResponse } from "@/types/response/GetUserResponse";

/**
 * Hook to fetch a single user by ID.
 *
 * The query is automatically disabled if no userId is provided.
 * Also handles language change based on user's selected language.
 *
 * @param userId - The unique identifier of the user to fetch
 *
 * @returns React Query result containing user data, loading state, and error state
 *
 * @example
 * ```tsx
 * const { data: user, isLoading } = useUser("user-123");
 * ```
 */
export function useUser(userId?: string | null) {
  const { i18n } = useTranslation();

  return useQuery<GetUserResponse, AxiosError>({
    queryKey: userQueryKeys.detail(userId ?? ""),
    queryFn: () =>
      instance.get(`/api/v2/Users/${userId}`).then((res) => {
        const langCode = res.data.selectedLanguage;
        if (langCode) {
          i18n.changeLanguage(langCode);
        }
        return res.data;
      }),
    enabled: !!userId,
    refetchOnReconnect: false,
    staleTime: 20 * 60 * 1000,
    retry: false,
  });
}
